import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const JsonFormatter: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [indentSize, setIndentSize] = useState(2);

  const formatJson = () => {
    try {
      setError('');
      const parsed = JSON.parse(input);
      const formatted = JSON.stringify(parsed, null, indentSize);
      setOutput(formatted);
    } catch (err) {
      setError('无效的 JSON 格式');
      setOutput('');
    }
  };

  const compressJson = () => {
    try {
      setError('');
      const parsed = JSON.parse(input);
      const compressed = JSON.stringify(parsed);
      setOutput(compressed);
    } catch (err) {
      setError('无效的 JSON 格式');
      setOutput('');
    }
  };

  const validateJson = () => {
    try {
      JSON.parse(input);
      setError('');
      setOutput('✅ JSON 格式有效');
    } catch (err) {
      setError('❌ JSON 格式无效');
      setOutput('');
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-4">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            返回首页
          </Link>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">JSON 在线格式化</h1>
          <p className="text-gray-600">格式化、压缩、验证 JSON 数据</p>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <button
              onClick={formatJson}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              格式化
            </button>
            <button
              onClick={compressJson}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              压缩
            </button>
            <button
              onClick={validateJson}
              className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              验证
            </button>
            <button
              onClick={clearAll}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              清空
            </button>
            <div className="flex items-center">
              <label className="mr-2 text-gray-700">缩进:</label>
              <select
                value={indentSize}
                onChange={(e) => setIndentSize(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1"
              >
                <option value={2}>2 空格</option>
                <option value={4}>4 空格</option>
                <option value={8}>8 空格</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Input/Output */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">输入 JSON</h3>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="请输入要格式化的 JSON 数据..."
              className="w-full h-96 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Output */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">输出结果</h3>
              {output && (
                <button
                  onClick={copyToClipboard}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm transition-colors"
                >
                  复制
                </button>
              )}
            </div>
            <textarea
              value={output}
              readOnly
              placeholder="格式化后的结果将显示在这里..."
              className="w-full h-96 p-4 border border-gray-300 rounded-lg resize-none bg-gray-50"
            />
          </div>
        </div>

        {/* Example */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">示例 JSON</h3>
          <button
            onClick={() => setInput(`{
  "name": "张三",
  "age": 25,
  "email": "zhangsan@example.com",
  "hobbies": ["读书", "游泳", "编程"],
  "address": {
    "city": "北京",
    "street": "朝阳区"
  }
}`)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            加载示例
          </button>
        </div>

        {/* Footer */}
        <div className="mt-16 text-center text-gray-500">
          <p className="mb-2">更多工具正在开发中，敬请期待...</p>
          <div className="flex items-center justify-center space-x-4 text-sm">
            <span>© 2024 在线工具集</span>
            <span>•</span>
            <a 
              href="https://beian.miit.gov.cn/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              湘ICP备2023018816号-1
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonFormatter;
